import { AzureSsoService }             from '../azure-sso/azure-sso.service';
import { BusinessIntelligenceService } from '../business-intelligence/business-intelligence.service';
import { CodeTypeModel }               from '../core/models/code-type';
import { GroupModel }                  from '../core/models/group';
import { UserModel }                   from './models/user';
import { UserNotificationTypeModel }   from './models/user.notification-type';

interface UserScope extends ng.IScope {
  block                           : ng.blockUI.BlockUIService;
  canResetTwoFactorAuth           : () => boolean;
  capricornUser                   : string;
  clear                           : ( field : string ) => void;
  currency                        : any;
  delete                          : ( form : ng.IFormController ) => void;
  form                            : ng.IFormController;
  groups                          : Array<GroupModel>;
  isAdvancedUser                  : () => boolean;
  isBusinessIntelligenceAvailable : () => boolean;
  isMobileMechanic                : () => boolean;
  loaded                          : boolean;
  mechanicSearch                  : any;
  mechanicSearchResultsTemplateUrl: string;
  notificationTypes               : Array<UserNotificationTypeModel>;
  originalBiStatus                : boolean;
  reconnectToAzure                : () => void;
  resetTwoFactorAuth              : ( form : ng.IFormController ) => void;
  save                            : ( form : any ) => void;
  status_types                    : Array<CodeTypeModel>;
  user                            : UserModel;
  userQuote                       : any;
}

export function UserCtrl (
  $q                              : ng.IQService,
  $rootScope                      : any,
  $scope                          : UserScope,
  $state                          : ng.ui.IStateService,
  $translate                      : ng.translate.ITranslateService,
  azureSsoApi                     : AzureSsoService,
  blockUI                         : ng.blockUI.BlockUIService,
  businessIntelligenceApi         : BusinessIntelligenceService,
  confirm                         : any,
  mechanicSearchResultsTemplateUrl: string,
  messages                        : any,
  notificationTypes               : Array<UserNotificationTypeModel>,
  sessionObjAPI                   : any,
  streamSearch                    : any,
  user                            : UserModel,
  userQuote                       : any,
  workshopAPI                     : any
) {
  $scope.user = user.id
    ? user
    : {
      dashboard_privileges: 'L',
      state               : 'ACTIVE'
    };

  $scope.block            = blockUI.instances.get('userBlock');
  $scope.capricornUser    = sessionObjAPI.base().capricorn_user;
  $scope.currency         = sessionObjAPI.currency();
  $scope.loaded           = true;
  $scope.originalBiStatus = $scope.user.business_intelligence_user;
  $scope.userQuote        = userQuote;

  $scope.user.confirm_password = $scope.user.password;

  $scope.mechanicSearchResultsTemplateUrl = mechanicSearchResultsTemplateUrl;
  $scope.notificationTypes                = notificationTypes;

  $scope.mechanicSearch = {
    list    : [],
    selected: {
      display_name: $scope.user.mechanic_display_name,
      id          : $scope.user.mechanic_id
    },
    search: ( typed : string ) => {
      return streamSearch.search('mechanics', typed)
      .then(( mechanics : Array<any> ) => $q.when($scope.mechanicSearch.list.concat(mechanics)));
    },
    format: ( mechanic : any ) => angular.isObject(mechanic) ? mechanic.display_name : mechanic,
    choose: ( mechanic : any ) => {
      $scope.user.mechanic_display_name = mechanic.display_name;
      $scope.user.mechanic_id           = mechanic.id;
    }
  };

  workshopAPI.get('/groups', ['*', '*'])
  .then(( groups : Array<GroupModel> ) => {
    $scope.groups = groups;
  });

  workshopAPI.get('/system/user_state_types')
  .then(( types : Array<CodeTypeModel> ) => {
    $scope.status_types = types;
  });

  $scope.canResetTwoFactorAuth = function () {
    return !!$scope.user.id
      && $rootScope.Company.isTwoFactorAuthEnabled();
  };

  $scope.clear = function (field) {
    $scope.user[field] = '';
  };

  $scope.delete = function(form) {
    confirm.generic($translate.instant('JS_SPACE.CONFIRM.DELETE_USER'))
    .then(() => workshopAPI.delete('/user', $scope.user.id))
    .then(() => {
      form.$setPristine();

      $state.go('app.users');

      messages.show($translate.instant('JS_SPACE.MESSAGES.DELETED.USER'), 'success');
    })
    .catch(err => {
      messages.error(err);
    });
  };

  $scope.isAdvancedUser = function () {
    return $scope.user.advanced_mobile_user;
  };

  $scope.isBusinessIntelligenceAvailable = function () {
    return businessIntelligenceApi.isAvailable();
  };

  $scope.isMobileMechanic = function () {
    return $scope.user.mobile_mechanic;
  };

  $scope.reconnectToAzure = function () {
    confirm.generic($translate.instant('JS_SPACE.CONFIRM.RECONNECT_TO_AZURE'))
    .then(() => azureSsoApi.reconnectUser($scope.user))
    .catch(err => {
      messages.error(err);
    });
  };

  $scope.resetTwoFactorAuth = function (form) {
    confirm.generic($translate.instant('JS_SPACE.CONFIRM.RESET_TFA'))
    .then(() => workshopAPI.post('/user/reset_two_factor_auth_validity', {
        id: $scope.user.id
    }))
    .then(() => {
      messages.show($translate.instant('JS_SPACE.MESSAGES.TFA_RESET'), 'success');

      form.$setPristine();

      $state.reload();
    })
    .catch(err => {
      messages.error(err);
    });
  };

  $scope.save = function (form) {
    $scope.block.start();

    if (!$scope.user.mobile_mechanic) {
      delete $scope.user.mechanic_id;
    }

    const notificationSaves : Array<Promise<any>> = [];

    angular.forEach($scope.notificationTypes, notification => {
      notificationSaves.push(workshopAPI.patch(`/user_mobile_notification_types/${ notification.id }/${ $scope.user.id }`, {
        enabled: notification.subscribed,
        id     : notification.id,
        user_id: $scope.user.id
      }));
    });

    $q.all(notificationSaves)
    .then(() => workshopAPI[
      $scope.user.id
      ? 'patch'
      : 'post'
    ]($scope.user.id
      ? '/user'
      : '/users', $scope.user, 'user'))
    .then(user => {
      $scope.user.id = user.id;

      return $scope.originalBiStatus !== $scope.user.business_intelligence_user
        ? businessIntelligenceApi.updateStatus($scope.user)
        : $q.when($scope.user);
    })
    .then(user => {
      messages.show($translate.instant('JS_SPACE.MESSAGES.SAVED.USER'), 'success');

      form.$setPristine();

      $state.go('app.user', {
        id: user.id
      }, { reload: true });
    })
    .catch(err => {
      messages.error(err);
    })
    .finally(() => {
      $scope.block.stop();
    });
  };
}